import type { SupabaseClient } from "@supabase/supabase-js";

import type { MessageCrewSite } from "./message-model.ts";

const CREW_SITE_COLUMNS = "site_id, site_name, assignment_status";
const CREW_SITE_LIMIT = 50;

export type MessageCrewSitesLoad =
  | { readonly status: "ready"; readonly value: readonly MessageCrewSite[] }
  | { readonly status: "failed"; readonly reason: string };

export async function fetchMessageCrewSites(
  client: SupabaseClient,
  accountId: string,
): Promise<MessageCrewSitesLoad> {
  try {
    const { data, error } = await client
      .from("my_shifts")
      .select(CREW_SITE_COLUMNS)
      .eq("account_id", accountId)
      .in("assignment_status", ["scheduled", "active"])
      .order("starts_at", { ascending: true })
      .limit(CREW_SITE_LIMIT);
    if (error !== null || !Array.isArray(data)) {
      return { status: "failed", reason: "Could not load your current sites. Try again." };
    }
    const sites = new Map<string, string>();
    for (const raw of data as readonly Record<string, unknown>[]) {
      const siteId = typeof raw.site_id === "string" ? raw.site_id.trim() : "";
      const siteName = typeof raw.site_name === "string" ? raw.site_name.trim() : "";
      const status = typeof raw.assignment_status === "string" ? raw.assignment_status : "";
      if (siteId === "" || siteName === "" || !["scheduled", "active"].includes(status)) continue;
      sites.set(siteId, siteName);
    }
    return {
      status: "ready",
      value: [...sites.entries()]
        .map(([siteId, siteName]) => ({ siteId, siteName }))
        .sort((left, right) => left.siteName.localeCompare(right.siteName)),
    };
  } catch {
    return { status: "failed", reason: "Could not load your current sites. Try again." };
  }
}
